/**
 * CardHub Wishlist Core Library
 * Vanilla JS library for Shopify theme integration
 * Handles localStorage (guest) + Supabase (logged in) sync
 */

(function() {
  'use strict';

  // Configuration - Update this with your API URL
  const CONFIG = {
    apiBaseUrl: (window.WISHLIST_API_URL || '').replace(/\/+$/, '') + '/api/wishlist',
    localStorageKey: 'cardhub_wishlist',
    localStorageListsKey: 'cardhub_wishlist_lists',
    cookieName: 'wishlist_migrated'
  };

  // State
  let customerId = null;
  let isLoggedIn = false;

  /**
   * Initialize the wishlist system
   * @param {Object} options - { customerId: string|null }
   */
  function init(options = {}) {
    customerId = options.customerId || null;
    isLoggedIn = !!customerId;

    // If logged in and has local items, trigger merge
    if (isLoggedIn && hasLocalItems() && !hasMigrated()) {
      mergeLocalToServer();
    }

    // Dispatch ready event
    window.dispatchEvent(new CustomEvent('wishlist:ready', { 
      detail: { isLoggedIn, customerId } 
    }));
  }

  // ============================================
  // LocalStorage Operations (Guest Mode)
  // ============================================

  function getLocalItems() {
    try {
      const data = localStorage.getItem(CONFIG.localStorageKey);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  function setLocalItems(items) {
    try {
      localStorage.setItem(CONFIG.localStorageKey, JSON.stringify(items));
    } catch (e) {
      console.error('[Wishlist] Failed to save to localStorage:', e);
    }
  }

  function hasLocalItems() {
    return getLocalItems().length > 0;
  }

  function hasMigrated() {
    return document.cookie.includes(CONFIG.cookieName + '=true');
  }

  function setMigrated() {
    document.cookie = CONFIG.cookieName + '=true; path=/; max-age=' + (60 * 60 * 24 * 365);
  }

  function clearLocalItems() {
    localStorage.removeItem(CONFIG.localStorageKey);
    localStorage.removeItem(CONFIG.localStorageListsKey);
  }

  // ============================================
  // API Helpers
  // ============================================

  async function apiRequest(endpoint, options = {}) {
    const url = CONFIG.apiBaseUrl + endpoint;
    const headers = {
      'Content-Type': 'application/json',
      ...(customerId ? { 'x-customer-id': customerId } : {})
    };

    try {
      const response = await fetch(url, {
        ...options,
        mode: 'cors',
        headers: { ...headers, ...options.headers }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      return data;
    } catch (error) {
      console.error('[Wishlist] API Error:', error);
      throw error;
    }
  }

  // ============================================
  // Core Wishlist Operations
  // ============================================

  /**
   * Add a product to wishlist
   * @param {Object} product - Product data
   * @returns {Promise<Object>} - Added item or local item
   */
  async function addItem(product) {
    const item = {
      product_handle: product.handle,
      product_id: product.id || null,
      product_title: product.title,
      product_image: product.image || null,
      product_price: product.price || null,
      game_type: product.game_type || 'other',
      note: product.note || null,
      notify_restock: product.notify_restock || false,
      added_at: new Date().toISOString()
    };

    if (isLoggedIn) {
      const result = await apiRequest('/items', {
        method: 'POST',
        body: JSON.stringify(item)
      });
      window.dispatchEvent(new CustomEvent('wishlist:added', { detail: result.item }));
      return result.item;
    } else {
      // Guest mode - store locally
      const items = getLocalItems();
      const exists = items.find(i => i.product_handle === item.product_handle);
      if (!exists) {
        items.push(item);
        setLocalItems(items);
      }
      window.dispatchEvent(new CustomEvent('wishlist:added', { detail: item }));
      return item;
    }
  }

  /**
   * Remove a product from wishlist
   * @param {string} productHandle - Product handle or item ID
   * @returns {Promise<boolean>}
   */
  async function removeItem(productHandle) {
    if (isLoggedIn) {
      // First check if we have the item ID
      const check = await apiRequest('/check?product_handle=' + encodeURIComponent(productHandle));
      if (check.item_id) {
        await apiRequest('/items/' + check.item_id, { method: 'DELETE' });
      }
    } else {
      const items = getLocalItems();
      const filtered = items.filter(i => i.product_handle !== productHandle);
      setLocalItems(filtered);
    }
    
    window.dispatchEvent(new CustomEvent('wishlist:removed', { detail: { product_handle: productHandle } }));
    return true;
  }

  /**
   * Check if a product is in wishlist
   * @param {string} productHandle
   * @returns {Promise<Object>} - { in_wishlist, item_id, list_id }
   */
  async function checkItem(productHandle) {
    if (isLoggedIn) {
      return await apiRequest('/check?product_handle=' + encodeURIComponent(productHandle));
    } else {
      const items = getLocalItems();
      const item = items.find(i => i.product_handle === productHandle);
      return {
        in_wishlist: !!item,
        item_id: null,
        list_id: null
      };
    }
  }

  /**
   * Batch check multiple products
   * @param {string[]} productHandles
   * @returns {Promise<Object>} - Map of handle -> status
   */
  async function checkItems(productHandles) {
    if (isLoggedIn) {
      const result = await apiRequest('/check', {
        method: 'POST',
        body: JSON.stringify({ product_handles: productHandles })
      });
      return result.results;
    } else {
      const items = getLocalItems();
      const results = {};
      productHandles.forEach(handle => {
        const item = items.find(i => i.product_handle === handle);
        results[handle] = {
          in_wishlist: !!item,
          item_id: null,
          list_id: null
        };
      });
      return results;
    }
  }

  /**
   * Get all wishlist items
   * @param {Object} options - { list_id, game_type, sort_by, sort_order }
   * @returns {Promise<Array>}
   */
  async function getItems(options = {}) {
    if (isLoggedIn) {
      const params = new URLSearchParams();
      if (options.list_id) params.set('list_id', options.list_id);
      if (options.game_type) params.set('game_type', options.game_type);
      if (options.sort_by) params.set('sort_by', options.sort_by);
      if (options.sort_order) params.set('sort_order', options.sort_order);

      const query = params.toString();
      const result = await apiRequest('/items' + (query ? '?' + query : ''));
      return result.items;
    } else {
      let items = getLocalItems();

      // Apply filters
      if (options.game_type && options.game_type !== 'all') {
        items = items.filter(i => i.game_type === options.game_type);
      }

      // Apply sorting
      const sortBy = options.sort_by || 'added_at';
      const sortOrder = options.sort_order || 'desc';
      items.sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];
        if (sortBy === 'product_price') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });

      return items;
    }
  }

  /**
   * Get wishlist count
   * @returns {Promise<number>}
   */
  async function getCount() {
    if (isLoggedIn) {
      const items = await getItems();
      return items.length;
    } else {
      return getLocalItems().length;
    }
  }

  // ============================================
  // List Operations (Logged in only)
  // ============================================

  /**
   * Get all lists
   * @returns {Promise<Array>}
   */
  async function getLists() {
    if (!isLoggedIn) {
      return [{ id: 'local', name: 'My Wishlist', item_count: getLocalItems().length }];
    }
    const result = await apiRequest('/lists');
    return result.lists;
  }

  /**
   * Create a new list
   * @param {string} name
   * @returns {Promise<Object>}
   */
  async function createList(name) {
    if (!isLoggedIn) {
      throw new Error('Must be logged in to create lists');
    }
    const result = await apiRequest('/lists', {
      method: 'POST',
      body: JSON.stringify({ name })
    });
    window.dispatchEvent(new CustomEvent('wishlist:list-created', { detail: result.list }));
    return result.list;
  }

  /**
   * Update a list
   * @param {string} listId
   * @param {Object} data - { name, is_public }
   * @returns {Promise<Object>}
   */
  async function updateList(listId, data) {
    if (!isLoggedIn) {
      throw new Error('Must be logged in to update lists');
    }
    const result = await apiRequest('/lists/' + listId, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
    window.dispatchEvent(new CustomEvent('wishlist:list-updated', { detail: result.list }));
    return result.list;
  }

  /**
   * Delete a list
   * @param {string} listId
   * @returns {Promise<boolean>}
   */
  async function deleteList(listId) {
    if (!isLoggedIn) {
      throw new Error('Must be logged in to delete lists');
    }
    await apiRequest('/lists/' + listId, { method: 'DELETE' });
    window.dispatchEvent(new CustomEvent('wishlist:list-deleted', { detail: { list_id: listId } }));
    return true;
  }

  /**
   * Move item to a different list
   * @param {string} itemId
   * @param {string} targetListId
   * @returns {Promise<Object>}
   */
  async function moveItem(itemId, targetListId) {
    if (!isLoggedIn) {
      throw new Error('Must be logged in to move items');
    }
    const result = await apiRequest('/items/' + itemId, {
      method: 'PATCH',
      body: JSON.stringify({ list_id: targetListId })
    });
    window.dispatchEvent(new CustomEvent('wishlist:item-moved', { detail: result.item }));
    return result.item;
  }

  /**
   * Update item note
   * @param {string} itemId
   * @param {string} note
   * @returns {Promise<Object>}
   */
  async function updateItemNote(itemId, note) {
    if (!isLoggedIn) {
      // Guest mode - update locally
      const items = getLocalItems();
      const item = items.find(i => i.product_handle === itemId);
      if (item) {
        item.note = note;
        setLocalItems(items);
      }
      return item;
    }
    const result = await apiRequest('/items/' + itemId, {
      method: 'PATCH',
      body: JSON.stringify({ note })
    });
    return result.item;
  }

  /**
   * Toggle restock notification for item
   * @param {string} itemId
   * @param {boolean} enabled
   * @returns {Promise<Object>}
   */
  async function toggleNotifyRestock(itemId, enabled) {
    if (!isLoggedIn) {
      throw new Error('Must be logged in to enable notifications');
    }
    const result = await apiRequest('/items/' + itemId, {
      method: 'PATCH',
      body: JSON.stringify({ notify_restock: enabled })
    });
    return result.item;
  }

  // ============================================
  // Notification Preferences
  // ============================================

  /**
   * Get notification preferences
   * @returns {Promise<Object>}
   */
  async function getNotificationPrefs() {
    if (!isLoggedIn) {
      return { email: null, digest_frequency: 'instant', enabled: false };
    }
    const result = await apiRequest('/notifications');
    return result.prefs;
  }

  /**
   * Save notification preferences
   * @param {Object} prefs - { email, digest_frequency, enabled }
   * @returns {Promise<Object>}
   */
  async function saveNotificationPrefs(prefs) {
    if (!isLoggedIn) {
      throw new Error('Must be logged in to save notification preferences');
    }
    const result = await apiRequest('/notifications', {
      method: 'POST',
      body: JSON.stringify(prefs)
    });
    return result.prefs;
  }

  // ============================================
  // Merge Operations
  // ============================================

  /**
   * Merge localStorage items to server on login
   */
  async function mergeLocalToServer() {
    if (!isLoggedIn) return;

    const localItems = getLocalItems();
    if (localItems.length === 0) return;

    try {
      const result = await apiRequest('/merge', {
        method: 'POST',
        body: JSON.stringify({ items: localItems })
      });

      // Clear local storage after successful merge
      clearLocalItems();
      setMigrated();

      window.dispatchEvent(new CustomEvent('wishlist:merged', { 
        detail: { merged: result.merged, skipped: result.skipped } 
      }));

      return result;
    } catch (error) {
      console.error('[Wishlist] Merge failed:', error);
      throw error;
    }
  }

  // ============================================
  // Share Operations
  // ============================================

  /**
   * Get share URL for a list
   * @param {string} listId
   * @returns {Promise<string>}
   */
  async function getShareUrl(listId) {
    if (!isLoggedIn) {
      throw new Error('Must be logged in to share lists');
    }
    const result = await apiRequest('/lists/' + listId + '/share', { method: 'POST' });
    return result.share_url;
  }

  // ============================================
  // Utility Functions
  // ============================================

  /**
   * Show notification using existing jQuery $.notify() plugin
   * Falls back to console if $.notify not available
   * @param {string} message
   * @param {string} type - 'success', 'warning', 'danger', 'info'
   */
  function showNotify(message, type = 'success') {
    if (typeof $ !== 'undefined' && typeof $.notify === 'function') {
      $.notify(
        { message: message },
        {
          type: type,
          offset: 0,
          placement: { from: 'top', align: 'center' },
          z_index: 9999,
          animate: {
            enter: 'animated fadeInDown',
            exit: 'animated fadeOutUp'
          },
          template: '<div data-notify="container" class="col-xs-12 alert alert-{0}" role="alert"><button type="button" aria-hidden="true" class="close" data-notify="dismiss">×</button><span data-notify="icon"></span> <span data-notify="title">{1}</span> <span data-notify="message">{2}</span><div class="progress" data-notify="progressbar"><div class="progress-bar progress-bar-{0}" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" style="width: 0%;"></div></div><a href="{3}" target="{4}" data-notify="url"></a></div>'
        }
      );
    } else {
      console.log('[Wishlist]', type.toUpperCase() + ':', message);
    }
  }

  /**
   * Toggle wishlist state for a product (data only - UI handled by caller)
   * @param {Object} product
   * @returns {Promise<{ added: boolean, item: Object|null }>}
   */
  async function toggle(product) {
    const handle = product.handle;
    const status = await checkItem(handle);
    
    if (status.in_wishlist) {
      await removeItem(handle);
      return { added: false, item: null };
    } else {
      const item = await addItem(product);
      return { added: true, item: item };
    }
  }

  // ============================================
  // Fetch Real-Time Prices
  // ============================================

  async function fetchPrices(handles) {
    if (!handles || handles.length === 0) return {};
    
    try {
      const response = await fetch(CONFIG.apiBaseUrl + '/prices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handles: handles })
      });
      
      if (!response.ok) {
        console.warn('[Wishlist] Failed to fetch prices:', response.status);
        return {};
      }
      
      const data = await response.json();
      return data.prices || {};
    } catch (error) {
      console.warn('[Wishlist] Error fetching prices:', error);
      return {};
    }
  }

  // ============================================
  // Export to Global
  // ============================================

  window.CardHubWishlist = {
    init,
    addItem,
    removeItem,
    checkItem,
    checkItems,
    getItems,
    getCount,
    getLists,
    createList,
    updateList,
    deleteList,
    moveItem,
    updateItemNote,
    toggleNotifyRestock,
    updateItemNotify: toggleNotifyRestock,
    getNotificationPrefs,
    saveNotificationPrefs,
    mergeLocalToServer,
    getShareUrl,
    fetchPrices,
    toggle,
    showNotify,
    isLoggedIn: () => isLoggedIn,
    getCustomerId: () => customerId,
    isInWishlistLocal: (handle) => getLocalItems().some(i => i.product_handle === handle)
  };

  // ============================================
  // Legacy Global Function Aliases
  // These allow existing shop.js code to work without modification
  // ============================================
  
  window.addProductToWishlist = async function(handle) {
    const result = await window.CardHubWishlist.addItem({ handle, product_handle: handle });
    return result;
  };

  window.removeProductFromWishlist = async function(handle) {
    await window.CardHubWishlist.removeItem(handle);
  };

  window.isInWishlist = async function(handle) {
    const status = await window.CardHubWishlist.checkItem(handle);
    return status.in_wishlist;
  };

  // Sync version for immediate checks (uses local cache)
  window.isInWishlistSync = function(handle) {
    return window.CardHubWishlist.isInWishlistLocal(handle);
  };

  // Dispatch ready event for shop.js to know CardHubWishlist is available
  window.dispatchEvent(new CustomEvent('wishlist:ready'));

})();
